/**
 * Dexie 缓存实现
 * 用于在浏览器中缓存 API 响应数据
 */
import Dexie, { Table } from 'dexie';

/**
 * 缓存项数据结构
 */
interface CacheItem {
  key: string; // 缓存键
  data: any; // 缓存数据
  timestamp: number; // 时间戳
}

/**
 * Dexie 数据库类
 */
class ApiCacheDatabase extends Dexie {
  cache!: Table<CacheItem>;

  constructor() {
    super('ExamFlowApiCacheDB');
    this.version(1).stores({
      cache: 'key, timestamp', // key 作为主键
    });
  }
}

// 创建数据库实例
const db = new ApiCacheDatabase();

/**
 * 生成缓存键
 * @param method HTTP 方法
 * @param endpoint API 端点
 * @param queryString 查询字符串（已排序）
 */
export function generateCacheKey(method: string, endpoint: string, queryString?: string): string {
  if (queryString) {
    return `${method}:${endpoint}:${queryString}`;
  }
  return `${method}:${endpoint}`;
}

/**
 * 从 Dexie 读取缓存
 * @param key 缓存键
 * @returns 缓存数据，如果不存在则返回 null
 */
export async function getCache<T>(key: string): Promise<T | null> {
  try {
    const item = await db.cache.get(key);
    if (!item) {
      return null;
    }
    return item.data as T;
  } catch (error) {
    console.error('[Cache] Failed to read cache:', key, error);
    return null;
  }
}

/**
 * 写入 Dexie 缓存
 * @param key 缓存键
 * @param data 要缓存的数据
 */
export async function setCache(key: string, data: any): Promise<void> {
  try {
    await db.cache.put({
      key,
      data,
      timestamp: Date.now(),
    });
  } catch (error) {
    console.error('[Cache] Failed to write cache:', key, error);
    // 不抛出错误，缓存失败不影响主流程
  }
}

/**
 * 删除指定缓存
 * @param key 缓存键
 */
export async function deleteCache(key: string): Promise<void> {
  try {
    await db.cache.delete(key);
  } catch (error) {
    console.error('[Cache] Failed to delete cache:', key, error);
  }
}

/**
 * 清空所有缓存
 */
export async function clearAllCache(): Promise<void> {
  try {
    await db.cache.clear();
  } catch (error) {
    console.error('[Cache] Failed to clear all cache:', error);
    throw error;
  }
}

/**
 * 根据前缀删除缓存
 * @param prefix 缓存键前缀（如 "GET:/api/banks"）
 */
export async function deleteCacheByPrefix(prefix: string): Promise<void> {
  try {
    const keys = await db.cache.where('key').startsWith(prefix).keys();
    await db.cache.bulkDelete(keys);
  } catch (error) {
    console.error('[Cache] Failed to delete cache by prefix:', prefix, error);
  }
}

/**
 * 获取缓存统计信息
 */
export async function getCacheStats(): Promise<{
  total: number;
  totalSize: number;
}> {
  try {
    const items = await db.cache.toArray();
    const totalSize = items.reduce((sum, item) => {
      return sum + JSON.stringify(item.data).length;
    }, 0);
    return {
      total: items.length,
      totalSize,
    };
  } catch (error) {
    console.error('[Cache] Failed to get cache stats:', error);
    return { total: 0, totalSize: 0 };
  }
}
